// Admin ActivityPage — full paginated activity feed as a table (dot,
// description, actor + role, entity type, absolute time), with an
// event_type filter. Dot colors match the dashboard card's DOT_COLOR map.
import { useState } from "react";
import { useFetch } from "../../hooks/useApi.js";
import Badge from "../../components/shared/Badge.js";
import { DOT_COLOR } from "../../components/admin/ActivityFeedRow.js";
import { formatDateTime } from "../../lib/formatDateTime.js";
import { ACTIVITY_EVENT_TYPES } from "@dm-lms/shared/types";
import type { ActivityLog, ActivityEventType, PaginatedResult } from "@dm-lms/shared/types";

type ActivityFeedItem = ActivityLog & { actorName: string };

function ActivitySkeletonTableRow() {
  return (
    <tr className="animate-pulse">
      <td className="px-4 py-3">
        <span className="block h-2 w-2 rounded-full bg-gray-200" />
      </td>
      <td className="px-4 py-3">
        <div className="h-3 w-56 rounded bg-gray-200" />
      </td>
      <td className="px-4 py-3">
        <div className="h-3 w-28 rounded bg-gray-200" />
      </td>
      <td className="px-4 py-3">
        <div className="h-3 w-20 rounded bg-gray-200" />
      </td>
      <td className="px-4 py-3">
        <div className="h-3 w-32 rounded bg-gray-200" />
      </td>
    </tr>
  );
}

export default function ActivityPage() {
  const [page, setPage] = useState(1);
  const [eventType, setEventType] = useState<ActivityEventType | "">("");

  const query = new URLSearchParams({ page: String(page), pageSize: "20" });
  if (eventType !== "") query.set("eventType", eventType);

  const { data, isLoading, error } = useFetch<PaginatedResult<ActivityFeedItem>>(
    `/api/admin/activity?${query.toString()}`
  );
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <div className="dm-page">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-dm-navy">Activity</h1>
        <select
          value={eventType}
          onChange={(event) => {
            setEventType(event.target.value as ActivityEventType | "");
            setPage(1);
          }}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm text-dm-navy focus:border-dm-royal focus:outline-none focus:ring-1 focus:ring-dm-royal"
        >
          <option value="">All event types</option>
          {ACTIVITY_EVENT_TYPES.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
      </div>

      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-dm-slate">
            <tr>
              <th className="px-4 py-3 font-medium" />
              <th className="px-4 py-3 font-medium">Description</th>
              <th className="px-4 py-3 font-medium">Actor</th>
              <th className="px-4 py-3 font-medium">Entity type</th>
              <th className="px-4 py-3 font-medium">Time</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <>
                <ActivitySkeletonTableRow />
                <ActivitySkeletonTableRow />
                <ActivitySkeletonTableRow />
                <ActivitySkeletonTableRow />
                <ActivitySkeletonTableRow />
              </>
            )}

            {!isLoading && !error && data?.items.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-sm text-dm-slate">
                  No activity yet.
                </td>
              </tr>
            )}

            {!isLoading &&
              data?.items.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3">
                    <span className={`block h-2 w-2 rounded-full ${DOT_COLOR[item.eventType] ?? "bg-gray-400"}`} />
                  </td>
                  <td className="px-4 py-3 text-dm-navy">{item.description}</td>
                  <td className="px-4 py-3">
                    <span className="mr-1.5 text-dm-navy">{item.actorName}</span>
                    <Badge tone="gray">{item.actorRole}</Badge>
                  </td>
                  <td className="px-4 py-3 text-dm-slate">{item.entityType ?? "—"}</td>
                  <td className="px-4 py-3 text-dm-slate">{formatDateTime(item.createdAt)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {data && (
        <div className="mt-4 flex items-center justify-between">
          <button
            type="button"
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-40"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </button>
          <span className="text-xs text-dm-slate">
            Page {data.page} of {totalPages} · {data.total} total
          </span>
          <button
            type="button"
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-40"
            disabled={!data.hasMore}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
